import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, TextField,
  Card, CardContent
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { dcApi } from '../services/dc.api'

const QTY_OVERRIDES_STORAGE_KEY = 'dc_qty_overrides'

function readQtyOverridesFromStorage(): Record<number, number> {
  try {
    const raw = localStorage.getItem(QTY_OVERRIDES_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export default function DCOrderCreatePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [notes, setNotes] = useState('')

  // Overrides passed via navigation state (primary) or persisted localStorage (fallback,
  // survives page reloads since it's not an in-memory-only cache)
  const navOverrides = (location.state as { qtyOverrides?: Record<number, number> } | null)?.qtyOverrides
  const displayOverrides = navOverrides ?? readQtyOverridesFromStorage()

  const overrideCount = Object.keys(displayOverrides).length
  const skippedCount = Object.values(displayOverrides).filter(v => v === 0).length

  const createMutation = useMutation({
    mutationFn: () => {
      // Read overrides at call time so we always get the latest
      const nav = (location.state as { qtyOverrides?: Record<number, number> } | null)?.qtyOverrides
      const qtyOverrides = nav ?? readQtyOverridesFromStorage()
      return dcApi.createOrder({ notes: notes || undefined, qtyOverrides })
    },
    onSuccess: (result) => {
      navigate(`/orders/${result.order.id}`)
    }
  })

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
          Back
        </Button>
        <Typography variant="h5">Create DC Order</Typography>
      </Box>

      <Card variant="outlined" sx={{ maxWidth: 600, mb: 3 }}>
        <CardContent>
          <Typography variant="body1" gutterBottom>
            This will create a new order from all active DC managed items with:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>
              <Typography variant="body2">14-day aggregated demand across all 3 stores</Typography>
            </li>
            <li>
              <Typography variant="body2">Order qty is net of current inventory (DC stock + all 3 stores) — items fully covered by stock are excluded automatically</Typography>
            </li>
            <li>
              <Typography variant="body2">Best distributor selected by lowest offer landing cost</Typography>
            </li>
            <li>
              <Typography variant="body2">Pricing snapshot from current DC price comparison</Typography>
            </li>
            {overrideCount > 0 ? (
              <li>
                <Typography variant="body2" color="primary">
                  {overrideCount} manual qty overrides applied on top of the computed net quantities
                  {skippedCount > 0 && ` · ${skippedCount} items forced to skip (qty = 0)`}
                </Typography>
              </li>
            ) : (
              <li>
                <Typography variant="body2" color="text.secondary">
                  No manual overrides — using computed (demand − inventory) for all items
                </Typography>
              </li>
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(createMutation.error as Error).message}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={createMutation.isPending ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            fullWidth
          >
            {createMutation.isPending ? 'Creating Order...' : 'Create Order'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}
